import type { MessageBus } from "./bus";
import { bus } from "./runtime";

let nextElementSource = 1;

/**
 * Shared base for all gr-* elements: bus access, unsubscribe tracking,
 * and the common design tokens. Pages theme components with CSS custom
 * properties on any ancestor:
 *
 *   :root { --gr-accent: #4ade80; --gr-bg: rgba(255,255,255,.04); }
 *
 * Element tiers (which folder a new element belongs in):
 *   components/  owns a side channel or resource beyond the bus —
 *                a transport connection, media stream, window routing
 *                (gr-connect, gr-input, gr-output)
 *   widgets/     pure bus endpoint with a rendered face; subscribe and
 *                publish only (gr-gauge, gr-toggle, gr-slider, gr-chart,
 *                gr-monitor)
 *   operators/   headless message transforms — message in, message out,
 *                no resources, no visuals (gr-link)
 */

export const TOKENS_CSS = `
  :host {
    --_accent: var(--gr-accent, #7dd3fc);
    --_bg: var(--gr-bg, rgba(255, 255, 255, 0.045));
    --_border: var(--gr-border, rgba(255, 255, 255, 0.1));
    --_text: var(--gr-text, rgba(255, 255, 255, 0.92));
    --_muted: var(--gr-muted, rgba(255, 255, 255, 0.45));
    --_radius: var(--gr-radius, 16px);
    --_font: var(--gr-font, Inter, ui-sans-serif, system-ui, -apple-system, sans-serif);
    --_mono: var(--gr-mono, ui-monospace, "SF Mono", Menlo, monospace);
    color: var(--_text);
    font-family: var(--_font);
    box-sizing: border-box;
  }
  :host *, :host *::before, :host *::after { box-sizing: inherit; }
  .label {
    color: var(--_muted);
    font-size: 10px;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    white-space: nowrap;
  }
`;

export abstract class GrElement extends HTMLElement {
  protected unsubscribers: Array<() => void> = [];
  private readonly sourceId = nextElementSource++;

  protected get bus(): MessageBus {
    return bus;
  }

  protected track(unsubscribe: () => void): void {
    this.unsubscribers.push(unsubscribe);
  }

  protected publisherSource(name: string): string {
    return `${name}:${this.sourceId}`;
  }

  disconnectedCallback(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
  }

  protected attr(name: string, fallback = ""): string {
    return this.getAttribute(name) ?? fallback;
  }

  protected numAttr(name: string, fallback: number): number {
    const value = Number(this.getAttribute(name));
    return Number.isNaN(value) || this.getAttribute(name) === null ? fallback : value;
  }
}
